import { navCollapsedWidth } from "@coherence-design-system/controls";
import { IStyle, ScrollablePane, mergeStyleSets } from "@fluentui/react";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import AdminNav from "./components/AdminNav";
import { AppInitializer } from "./components/AppInitializer";
import { DataResearchDocumentViewer } from "./components/DataResearchDocumentViewer";
import FloatingCopilotButton from "./components/FloatingCopilotButton";
import HeaderComponent from "./components/HeaderComp";
import FullPageReportView from "./components/Report/FullPageReportView";
import ReportView from "./components/Report/ReportView";
import Search from "./components/Search";
import AccessDenied from "./helper/AccessDenied";
import { AIPlaygroundPage } from "./pages/AIPlaygroundPage";
import AnnouncmentPage from "./pages/AnnoucementPage";
import CacheAdminPage from "./pages/CacheAdminPage";
import CopilotChat from "./pages/CopilotChat";
import CustomerPage from "./pages/CustomerPage";
import CustomerSearch from "./pages/CustomerSearch";
import { DataResearchPage } from "./pages/DataResearchPage";
import { ExploreProducts } from "./pages/ExploreProducts";
import ExploreReports from "./pages/ExploreReports";
import ExploreSupport from "./pages/ExploreSupport";
import FeatureFlowPage from "./pages/FeatureFlowPage";
import FeatureRequestSearch from "./pages/FeatureRequestSearch";
import Glossary from "./pages/Glossary";
import HealthCheckPage from "./pages/HealthCheckPage"; // Import the new HealthCheckPage
import HomePage from "./pages/HomePage";
import MyFavorites from "./pages/MyFavorites";
import PageNotFound from "./pages/PageNotFound";
import PartnerPage from "./pages/PartnerPage";
import PartnerSearch from "./pages/PartnerSearch";
import ProductScorecard from "./pages/ProductScorecard";
import ProductPage from "./pages/ProductView";
import SupportReports from "./pages/SupportReportsPage";
import SupportReportView from "./pages/SupportReportView";
import ExploreHealthPage from "./pages/ExploreHealth";
import CloseTheLoopPage from "./pages/CloseTheLoopPage";
import { fetchVerifyUser } from "./Services/user-loging.services";
import { selectAuthUser } from "./slices/Auth.slice";
import { useGetUIEnablementsSettingsQuery, useLazyGetUIEnablementsSettingsQuery } from "./slices/Home.slice";
import { updateTarget } from "./slices/UI.slice";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { buildDisabledFeatureLookup, getDisabledRoutes } from "./Utils/js-common";
import useResizeObserver from "./Utils/resize-observer";
import BuildWithCustomerPage from "./pages/BuildWithCustomerPage";
import PurviewCVCPage from "./pages/PurviewCVCPage";
import CVCCustomerPage from "./pages/CVCCustomerPage";
import CustomerPerceptionPage from "./pages/CustomerPerceptionPage";

export const scrollablePaneStyles = {
  position: "fixed",
  top: 48,
  bottom: 0,
  right: 0
};

const scrollBarWidth = 0;

export const calculcateScrollBarWidth = () => {
  return scrollBarWidth;
};

export const styles = mergeStyleSets({
  scrollablePaneCollapsed: {
    ...scrollablePaneStyles,
    left: navCollapsedWidth + calculcateScrollBarWidth() + 10
  } as IStyle,
  scrollablePaneExpand: {
    ...scrollablePaneStyles,
    left: 228 + calculcateScrollBarWidth() + 10
  } as IStyle,
  scrollablePaneBackgroundForProductPage: {
    backgroundColor: "#fff",
    inset: "48px 0px 0px 48px"
  } as IStyle,
  rootDiv: {
    paddingRight: "30px",
    paddingLeft: "10px",
    display: "flex",
    flexDirection: "row"
  } as IStyle,
  dividerLine: {
    width: "100%",
    height: "1px",
    backgroundColor: "black",
    marginBottom: "20px"
  } as IStyle,
  rowGap: {
    height: "30px"
  } as IStyle
});

const NavComponent = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const onMouseEnter = useCallback(() => {
    setIsNavCollapsed(false);
  }, []);
  const onMouseLeave = useCallback(() => {
    setIsNavCollapsed(true);
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      const rowGroupElements = document.querySelectorAll("[role='menubar']");
      rowGroupElements.forEach(function (element) {
        element.setAttribute("aria-busy", "true");
      });
    }, 1000);
  }, [isNavCollapsed]);
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <AdminNav isNavCollapsed={isNavCollapsed}></AdminNav>
    </div>
  );
};

const MainContainer = () => {
  const { pathname } = useLocation();
  const isProductPage = ["products", "productview"].some((name) => pathname.includes(name));
  const renderClassNames = `${styles.scrollablePaneCollapsed} ${isProductPage ? styles.scrollablePaneBackgroundForProductPage : ""}`;
  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabledRoutes = getDisabledRoutes(uiEnablementData);

  // Helper to conditionally render route
  const renderRoute = (path: string, element: React.ReactNode) => {
    if (disabledRoutes.has(path)) {
      return null; // Route is disabled, don't render
    }
    return <Route path={path} element={element} />;
  };

  return (
    <main id="main" tabIndex={-1}>
      <ScrollablePane className={renderClassNames}>
        <Routes>
          {/* ...existing routes... */}
          <Route path="/" element={<HomePage />} />

          {/* Conditionally rendered routes based on disableConfig */}
          {/* Explore Customers Feature */}
          {renderRoute("/customersearch", <CustomerSearch />)}
          {renderRoute("/customer/:id?", <CustomerPage />)}
          {/* Explore Products Feature */}
          {renderRoute("/products", <ExploreProducts />)}
          {renderRoute("/productview", <ProductPage />)}
          {/* Explore Health Feature */}
          {renderRoute("/explorehealth", <ExploreHealthPage />)}
          {/* Explore Reports Feature */}
          {renderRoute("/explorereports/:tab?", <ExploreReports />)}
          {renderRoute("/reportview/:reportId?", <FullPageReportView />)}
          {/* Explore Support Feature */}
          {renderRoute("/exploresupport", <ExploreSupport />)}
          {renderRoute("/supportreports/:category?", <SupportReports />)}
          {renderRoute("/supportreportview/:reportId?", <SupportReportView />)}
          {/* Explore Partners Feature */}
          {renderRoute("/partnersearch", <PartnerSearch />)}
          {renderRoute("/partners", <PartnerPage />)}
          {/* Explore Feature Requests Feature */}
          {renderRoute("/featurerequestsearch", <FeatureRequestSearch />)}
          {renderRoute("/featureflow/:id?", <FeatureFlowPage />)}
          {/* My Favorites Feature */}
          {renderRoute("/myfavorites", <MyFavorites />)}
          {/* Data Research Feature */}
          {renderRoute("/dataresearch", <DataResearchPage />)}
          {renderRoute("/dataresearchview/:id?", <DataResearchDocumentViewer />)}
          {/* AI Playground Feature */}
          {renderRoute("/aihub", <AIPlaygroundPage />)}
          {renderRoute("/aichat", <CopilotChat />)}
          {/* Product Scorecard Feature */}
          {renderRoute("/productscorecard", <ProductScorecard />)}
          {/* Build With Customer Feature */}
          {renderRoute("/buildwithcustomer", <BuildWithCustomerPage />)}
          {/* Purview CVC Feature */}
          {renderRoute("/purviewcvc", <PurviewCVCPage />)}
          {/* CVC Customer Feature */}
          {renderRoute("/cvccustomers", <CVCCustomerPage />)}
          {/* Customer Perception Insights Feature */}
          {renderRoute("/customerperception", <CustomerPerceptionPage />)}
          {/* Close The Loop Feature */}
          {renderRoute("/closetheloop", <CloseTheLoopPage />)}

          {/* ...rest of routes... */}
          {/* Cache Admin - access controlled within component */}
          <Route path="/cacheadmin" element={<CacheAdminPage />} />
          <Route path="/modalreportview/:reportid?" element={<ReportView />} />
          <Route path="/search/:q?" element={<Search />} />
          <Route path="/announcement" element={<AnnouncmentPage />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/healthcheck" element={<HealthCheckPage />} />
          <Route path="/404" Component={PageNotFound} />
          <Route path="*" Component={PageNotFound} />
        </Routes>
      </ScrollablePane>
    </main>
  );
};

const VerifyUserOrMainContainer = ({ children }: PropsWithChildren) => {
  const authenticatedUser = useAppSelector((state) => selectAuthUser(state));
  const isAuthorizedUser = useMemo(() => {
    const authorizedRoles = ["Admin", "User"];
    const roles = authenticatedUser?.roles ?? [];
    return authorizedRoles.some((role) => roles.includes(role));
  }, [authenticatedUser]);

  useEffect(() => {
    fetchVerifyUser(authenticatedUser?.accessToken).then();
  }, [authenticatedUser]);

  return (
    <ScrollablePane className={styles.scrollablePaneCollapsed} styles={{ root: { inset: "48px 0px 0px 0px" } }}>
      {isAuthorizedUser ? <>{children}</> : <AccessDenied />}
    </ScrollablePane>
  );
};

const MainComp = () => {
  const dispatch = useAppDispatch();
  const onResize = useCallback(
    (target: HTMLDivElement) => {
      dispatch(updateTarget(target));
    },
    [dispatch]
  );
  const [triggerUIEnablements] = useLazyGetUIEnablementsSettingsQuery();

  const ref = useResizeObserver(onResize);

  const handleInitialize = useCallback(async () => {
    // Fire the UI enablements fetch in the background so it never blocks app
    // rendering. If it is slow or fails, the app still loads: route/feature
    // gating falls back to "nothing disabled" until the data arrives, and the
    // useGetUIEnablementsSettingsQuery hooks update the UI reactively when it does.
    triggerUIEnablements(undefined);
  }, [triggerUIEnablements]);

  return (
    <div ref={ref}>
      <BrowserRouter>
        <HeaderComponent />
        <VerifyUserOrMainContainer>
          <AppInitializer onInitialize={handleInitialize}>
            <NavComponent />
            <MainContainer />
          </AppInitializer>
        </VerifyUserOrMainContainer>
        <CopilotButtonWrapper />
      </BrowserRouter>
    </div>
  );
};

// Wrapper component to handle conditional rendering based on route
const CopilotButtonWrapper: React.FC = () => {
  const location = useLocation();
  const { data = [], isLoading, isError } = useGetUIEnablementsSettingsQuery(undefined);
  const disabled = buildDisabledFeatureLookup(data);
  const disabledFeatures = disabled["feature"] || [];
  const isCopilotDisabled = disabledFeatures.some((item) => item.feature === "Copilot" && item.isDisabled);

  // Show copilot button on the customer page and product view routes
  const isCustomerPage = location.pathname.startsWith("/customer") && !location.pathname.startsWith("/customersearch");
  const isProductViewPage = location.pathname.startsWith("/productview");

  const shouldRenderCopilotButton =
    !isCopilotDisabled &&
    (isCustomerPage || isProductViewPage) &&
    // Preserve existing behavior: hide while loading (unless error is present)
    !(isLoading && !isError);

  return shouldRenderCopilotButton ? <FloatingCopilotButton /> : null;
};

export default MainComp;
